// import { PlusOutlined } from '@ant-design/icons';
import {
  type ActionType,
  type ProColumns,
  ProTable,
  type ProTableProps,
} from '@ant-design/pro-components';
import { Button, message, Popconfirm } from 'antd';
import type { Rule } from 'antd/es/form';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { MyButton, MyModalForm } from '@/components';
import { listToTree } from '@/lib';
import { apiClient } from '@/services';

export type FormScene = 'create' | 'edit' | 'search';

export type MyValueType = ProColumns<any>['valueType'] | 'icon'; //  关键：加 icon

export type MyProColumn<T> = Omit<ProColumns<T>, 'valueType'> & {
  /** 是否在新增表单隐藏 */
  hideInCreate?: boolean;
  /** 是否在编辑表单隐藏 */
  hideInEdit?: boolean;
  /** 是否在表单中可编辑（总开关） */
  editable?: boolean;
  /** 编辑场景下禁用输入（常见：username 不可改） */
  editDisabled?: boolean;
  /** 新增场景下禁用输入 */
  createDisabled?: boolean;

  /** 表单校验规则（优先级高于 rules） */
  formRules?: Rule[] | ((scene: FormScene, record?: Partial<T>) => Rule[]);
  /** 传给 ProFormItem 的 props */
  formItemProps?: any | ((scene: FormScene, record?: Partial<T>) => any);

  /** 新增时默认值（表格列的 initialValue 不一定适合表单） */
  createDefaultValue?: any;
  valueType?: MyValueType;
};

type BaseProTableProps<T extends Record<string, any>> = Omit<
  ProTableProps<T, any, any>,
  'columns' | 'title' | 'rowKey' | 'request'
>;

export interface MyProTableProps<T extends Record<string, any>>
  extends BaseProTableProps<T> {
  rowKey: string;
  columns: MyProColumn<T>[];
  loadData: (params: any) => Promise<{
    data: { list: T[]; total: number };
    code: number;
  }>;

  baseUrl: string;
  title?: string;
  hideIndexColumn?: boolean;
  hideOptColumn?: boolean;
  treeTable?: boolean;
  parentId?: string;
  optExtraBtns?: (record: T) => React.ReactNode[];
  toolBar?: React.ReactNode[]; // 自定义工具栏
  hideEditBtn?: boolean; // 隐藏编辑按钮
  hideAddBtn?: boolean; // 隐藏新增按钮
  requestParams?: object; // 请求带默认参数
}

export type MyProTableRef<T> = {
  getEditingRecord: () => T | undefined;
  setEditingRecord: (r?: T) => void;
  reload: () => void;
};

function MyProTableInner<T extends Record<string, any>>(
  props: MyProTableProps<T>,
  ref: React.ForwardedRef<MyProTableRef<T>>,
) {
  const {
    rowKey,
    columns,
    loadData,
    baseUrl,
    title = '',
    hideIndexColumn = false,
    hideOptColumn = false,
    treeTable = false,
    parentId = 'parentId',
    optExtraBtns = () => [],
    toolBar,
    hideAddBtn = false,
    hideEditBtn = false,
    requestParams = {},
    ...restProps
  } = props;

  const actionRef = useRef<ActionType>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<T | undefined>();
  const [selectedRowKey, setSelectedRowKey] = useState<React.Key | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      getEditingRecord: () => editingRecord,
      setEditingRecord: (r: T | undefined) => {
        setEditingRecord(r);
      },
      reload: () => actionRef.current?.reload(),
    }),
    [editingRecord],
  );

  const handleSubmit = async (values: T) => {
    const hide = message.loading('保存中...');
    let res: any;

    let newValues: any = values;
    // 处理一下undefined 接口会自动过滤掉
    Object.keys(newValues).forEach((key) => {
      if (newValues[key] === undefined) newValues[key] = null;
    });

    if (editingRecord) newValues = { ...editingRecord, ...values };

    if (editingRecord?.[rowKey]) {
      res = await apiClient.put<{ code: number }>(
        `${baseUrl}/${editingRecord[rowKey]}`,
        newValues,
      );
    } else {
      res = await apiClient.post<{ code: number }>(baseUrl, newValues);
    }
    if (res?.code === 200) {
      hide();
      message.success(editingRecord?.[rowKey] ? '保存成功' : '新增成功');
      setModalVisible(false);
      setEditingRecord(undefined);
      actionRef.current?.reload();
      return true;
    } else {
      //  hide();
      // message.error('操作失败');
      return false;
    }
  };

  // 设置column
  const getColumn = () => {
    const opt: ProColumns<T> = {
      title: '操作',
      valueType: 'option',
      // width: 80,
      render: (_, record) => [
        ...optExtraBtns(record),
        !hideEditBtn && (
          <a
            key="edit"
            onClick={() => {
              setEditingRecord(record);
              setModalVisible(true);
            }}
          >
            编辑
          </a>
        ),
        <Popconfirm
          key="delete"
          title="确认删除？"
          onConfirm={() => {
            apiClient.delete(`${baseUrl}/${record[rowKey]}`).then(() => {
              actionRef.current?.reload();
              setSelectedRowKey(null);
            });
          }}
        >
          <a style={{ color: 'red' }}>删除</a>
        </Popconfirm>,
      ],
    };

    const newColumns = [...columns].map((item) => {
      if (item.valueType === 'icon') item.valueType = 'text';
      return item;
    }) as ProColumns[];
    if (!hideOptColumn) newColumns.push(opt);
    if (!hideIndexColumn) {
      newColumns.unshift({
        title: '序号',
        dataIndex: 'index',
        render: (_: any, __: any, index: number) => index + 1,
        width: 60,
        hideInSearch: true,
      });
    }

    return newColumns;
  };

  // 工具栏
  const toolBarRender = () => {
    if (toolBar) return toolBar;

    if (treeTable) {
      //  tree 模式
      return [
        <MyButton
          key="add-sibling"
          btnType="add"
          onClick={() => {
            // setEditingRecord({
            //   // 新增同级：parentId = 当前行的 parentId
            //   [parentId]: '0',
            // } as any);
            setModalVisible(true);
          }}
        >
          新增同级
        </MyButton>,
        <MyButton
          key="add-child"
          btnType="add"
          disabled={!selectedRowKey}
          onClick={() => {
            if (!selectedRowKey) return;
            setEditingRecord({
              // 新增下级：parentId = 当前行 id
              [parentId]: selectedRowKey,
            } as any);
            setModalVisible(true);
          }}
        >
          新增子级
        </MyButton>,
      ];
    } else {
      // 列表
      if (!hideAddBtn) {
        return [
          <MyButton
            key="add"
            btnType="add"
            onClick={() => {
              setEditingRecord(undefined);
              setModalVisible(true);
            }}
          />,
        ];
      }
    }

    return [];
  };

  const request = async (params: any) => {
    const res = await loadData({ ...requestParams, ...params });
    let newData = res.data.list || [];
    if (treeTable) {
      newData = listToTree(newData, {
        id: rowKey,
      });
    }

    return {
      data: newData,
      total: res.data.total,
      success: res.code === 200,
    };
  };

  return (
    <>
      <ProTable<T>
        rowKey={rowKey}
        actionRef={actionRef}
        columns={getColumn()}
        headerTitle={`${title}列表`}
        pagination={treeTable ? false : { pageSize: 10 }}
        toolBarRender={toolBarRender}
        request={request}
        // 点击行
        onRow={(record) => {
          return {
            onClick: () => {
              setSelectedRowKey(record[rowKey]);
            },
          };
        }}
        // 根据 key 返回 class
        rowClassName={(record) =>
          record[rowKey] === selectedRowKey ? 'ant-table-row-selected' : ''
        }
        expandable={{
          defaultExpandAllRows: true, // 默认展开全部
          expandIconColumnIndex: 1, // 第 2 列（从 0 开始）
        }}
        {...restProps}
      />

      <MyModalForm<T>
        open={modalVisible}
        tableTitle={title}
        initialValues={editingRecord}
        columns={columns}
        onFinish={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          setEditingRecord(undefined);
        }}
      />
    </>
  );
}

export const MyProTable = forwardRef(MyProTableInner) as <
  T extends Record<string, any>,
>(
  props: MyProTableProps<T> & { ref?: React.Ref<MyProTableRef<T>> },
) => React.ReactElement;
